import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Edit, Trash2, Plus, LogOut, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { optimizeImage, sanitizeText, formatPrice, getBase64Size } from '../utils/optimization';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function AdminPage() {
  const { t, language } = useLanguage();
  const { isAdmin, login, logout } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    name: { sr: '', en: '', ru: '' },
    description: { sr: '', en: '', ru: '' },
    price: '',
    image: ''
  });

  useEffect(() => {
    if (isAdmin) {
      loadProducts();
    }
  }, [isAdmin]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/products`);
      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    const success = login(loginForm.username, loginForm.password);
    if (success) {
      toast.success(t('admin.products.loginSuccess'));
      setLoginError('');
    } else {
      setLoginError(t('admin.login.error'));
    }
  };

  const handleLogout = () => {
    logout();
    toast.success(t('admin.products.logoutSuccess'));
    navigate('/');
  };

  const handleInputChange = (e, lang = null) => {
    const { name, value } = e.target;
    if (lang) {
      setFormData(prev => ({
        ...prev,
        [name]: { ...prev[name], [lang]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t('admin.products.imageTooLarge'));
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error(t('admin.products.invalidImageType'));
        return;
      }

      // Optimize image automatically
      toast.info(t('admin.products.optimizingImage') || 'Optimizing image...');
      
      try {
        const optimizedImage = await optimizeImage(file, {
          maxWidth: 800,
          maxHeight: 800,
          quality: 0.8,
          format: 'image/jpeg'
        });
        
        const sizeKB = getBase64Size(optimizedImage);
        setFormData(prev => ({ ...prev, image: optimizedImage }));
        toast.success(`${t('admin.products.imageUploaded')} (${sizeKB}KB)`);
      } catch (error) {
        console.error('Image optimization failed:', error);
        // Fallback to original image
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData(prev => ({ ...prev, image: reader.result }));
          toast.success(t('admin.products.imageUploaded'));
        };
        reader.readAsDataURL(file);
      }
    }
  };

  // Sanitize text input on blur
  const handleTextBlur = (e, lang = null) => {
    const { name, value } = e.target;
    const maxLength = name === 'description' ? 500 : 100;
    const sanitized = sanitizeText(value, { maxLength });
    
    if (lang) {
      setFormData(prev => ({
        ...prev,
        [name]: { ...prev[name], [lang]: sanitized }
      }));
    } else if (name === 'price') {
      setFormData(prev => ({ ...prev, [name]: formatPrice(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: sanitized }));
    }
  };

  const handleAddProduct = () => {
    setIsAdding(true);
    setEditingProduct(null);
    setFormData({
      name: { sr: '', en: '', ru: '' },
      description: { sr: '', en: '', ru: '' },
      price: '',
      image: ''
    });
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product.id);
    setIsAdding(false);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price,
      image: product.image
    });
  };

  const handleSaveProduct = async () => {
    if (!formData.name.en || !formData.description.en || !formData.price) {
      toast.error(t('admin.products.requiredFields'));
      return;
    }

    try {
      setSaving(true);
      
      if (isAdding) {
        // Create new product
        const response = await fetch(`${BACKEND_URL}/api/products`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });
        
        if (!response.ok) {
          throw new Error('Failed to create product');
        }
        
        toast.success(t('admin.products.addSuccess'));
      } else {
        // Update existing product
        const response = await fetch(`${BACKEND_URL}/api/products/${editingProduct}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });
        
        if (!response.ok) {
          throw new Error('Failed to update product');
        }
        
        toast.success(t('admin.products.updateSuccess'));
      }

      // Reload products from server
      await loadProducts();
      
      setIsAdding(false);
      setEditingProduct(null);
      setFormData({
        name: { sr: '', en: '', ru: '' },
        description: { sr: '', en: '', ru: '' },
        price: '',
        image: ''
      });
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products/${id}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete product');
      }
      
      // Reload products from server
      await loadProducts();
      setDeleteConfirm(null);
      toast.success(t('admin.products.deleteSuccess'));
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingProduct(null);
    setFormData({
      name: { sr: '', en: '', ru: '' },
      description: { sr: '', en: '', ru: '' },
      price: '',
      image: ''
    });
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl text-center">{t('admin.login.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">{t('admin.login.username')}</Label>
                <Input
                  id="username"
                  type="text"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                  required
                  placeholder="admin"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t('admin.login.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  required
                  placeholder="••••••••"
                />
              </div>
              {loginError && (
                <p className="text-destructive text-sm">{loginError}</p>
              )}
              <Button type="submit" className="w-full btn-honey">
                {t('admin.login.submit')}
              </Button>
            </form>
            <div className="mt-6 p-4 bg-secondary/50 rounded-lg">
              <p className="text-sm text-muted-foreground text-center">
                <strong>{t('admin.login.demoCredentials')}:</strong><br />
                {t('admin.login.demoUsername')}: admin<br />
                {t('admin.login.demoPassword')}: markovic2024
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <section className="bg-gradient-to-r from-primary to-[hsl(42,100%,65%)] text-white py-12">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl sm:text-4xl font-bold">{t('admin.title')}</h1>
            <Button 
              onClick={handleLogout}
              variant="outline"
              className="border-white text-white bg-white/10 hover:bg-white hover:text-foreground"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t('admin.logout')}
            </Button>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-foreground">{t('admin.products.title')}</h2>
              <Button onClick={handleAddProduct} className="btn-honey">
                <Plus className="w-4 h-4 mr-2" />
                {t('admin.products.add')}
              </Button>
            </div>

            {/* Add/Edit Form */}
            {(isAdding || editingProduct) && (
              <Card className="mb-8 bg-card border-primary/20 shadow-lg animate-scale-in">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-6 text-foreground">
                    {isAdding ? t('admin.products.add') : t('admin.products.edit')}
                  </h3>
                  <div className="space-y-6">
                    {/* Name in all languages */}
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>{t('admin.products.name')} ({t('admin.products.serbian')})</Label>
                        <Input
                          name="name"
                          value={formData.name.sr}
                          onChange={(e) => handleInputChange(e, 'sr')}
                          onBlur={(e) => handleTextBlur(e, 'sr')}
                          placeholder="Livadski Med"
                          maxLength={100}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('admin.products.name')} ({t('admin.products.english')})</Label>
                        <Input
                          name="name"
                          value={formData.name.en}
                          onChange={(e) => handleInputChange(e, 'en')}
                          onBlur={(e) => handleTextBlur(e, 'en')}
                          placeholder="Meadow Honey"
                          maxLength={100}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('admin.products.name')} ({t('admin.products.russian')})</Label>
                        <Input
                          name="name"
                          value={formData.name.ru}
                          onChange={(e) => handleInputChange(e, 'ru')}
                          onBlur={(e) => handleTextBlur(e, 'ru')}
                          placeholder="Луговой Мед"
                          maxLength={100}
                        />
                      </div>
                    </div>

                    {/* Description in all languages */}
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>{t('admin.products.description')} ({t('admin.products.serbian')})</Label>
                        <Textarea
                          name="description"
                          value={formData.description.sr}
                          onChange={(e) => handleInputChange(e, 'sr')}
                          onBlur={(e) => handleTextBlur(e, 'sr')}
                          rows={3}
                          maxLength={500}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('admin.products.description')} ({t('admin.products.english')})</Label>
                        <Textarea
                          name="description"
                          value={formData.description.en}
                          onChange={(e) => handleInputChange(e, 'en')}
                          onBlur={(e) => handleTextBlur(e, 'en')}
                          rows={3}
                          maxLength={500}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('admin.products.description')} ({t('admin.products.russian')})</Label>
                        <Textarea
                          name="description"
                          value={formData.description.ru}
                          onChange={(e) => handleInputChange(e, 'ru')}
                          onBlur={(e) => handleTextBlur(e, 'ru')}
                          rows={3}
                          maxLength={500}
                        />
                      </div>
                    </div>

                    {/* Price and Image */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>{t('admin.products.price')}</Label>
                        <Input
                          name="price"
                          value={formData.price}
                          onChange={handleInputChange}
                          onBlur={handleTextBlur}
                          placeholder="1,200 RSD / 500g"
                          required
                        />
                        <p className="text-xs text-muted-foreground">Format: 1,200 RSD / 500g</p>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('admin.products.image')}</Label>
                        <div className="flex gap-2">
                          <Input
                            name="image"
                            value={formData.image && !formData.image.startsWith('data:') ? formData.image : ''}
                            onChange={handleInputChange}
                            placeholder="https://..."
                            className="flex-1"
                          />
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleImageUpload}
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            title={t('admin.products.uploadImage')}
                          >
                            <Upload className="w-4 h-4" />
                          </Button>
                        </div>
                        {formData.image && (
                          <div className="mt-2">
                            <img 
                              src={formData.image} 
                              alt="Preview" 
                              className="w-24 h-24 object-cover rounded border"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <Button onClick={handleSaveProduct} className="btn-honey" disabled={saving}>
                        {saving ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {t('admin.products.save')}...
                          </>
                        ) : (
                          t('admin.products.save')
                        )}
                      </Button>
                      <Button onClick={handleCancel} variant="outline" disabled={saving}>
                        {t('admin.products.cancel')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Products List */}
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
                <p className="text-muted-foreground mt-4">Loading products...</p>
              </div>
            ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => (
                <Card key={product.id} className="bg-card border-border shadow-md">
                  <div className="aspect-square overflow-hidden bg-secondary/50">
                    <img 
                      src={product.image} 
                      alt={product.name[language] || product.name.en}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1587049352851-8d4e89133924';
                      }}
                    />
                  </div>
                  <CardContent className="p-4">
                    <h3 className="text-lg font-semibold mb-2 text-foreground">
                      {product.name[language] || product.name.en}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {product.description[language] || product.description.en}
                    </p>
                    <p className="text-primary font-semibold mb-4">{product.price}</p>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleEditProduct(product)}
                        size="sm"
                        variant="outline"
                        className="flex-1"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        {t('admin.products.edit')}
                      </Button>
                      <Button 
                        onClick={() => setDeleteConfirm(product.id)}
                        size="sm"
                        variant="destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            )}
          </div>
        </div>
      </section>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.products.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.products.deleteConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('admin.products.cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDeleteProduct(deleteConfirm)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('admin.products.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}